import {isEqual} from 'lodash';
import {
  ElementRef, forwardRef, ForwardRefRenderFunction, memo, ReactNode, useEffect, useImperativeHandle,
  useRef
} from 'react';
import {renderToString} from 'react-dom/server';

interface IProps extends naver.maps.MarkerOptions {
  children: ReactNode;
}

interface IHandle {
  instance: naver.maps.Marker | null;
}

const StaticMarkerFRRF: ForwardRefRenderFunction<IHandle, IProps> = (
  {children, ...options},
  ref
) => {
  const instance = useRef<naver.maps.Marker | null>(null);
  const prevRef = useRef<naver.maps.MarkerOptions>();

  const removeInstance = () => {
    if (!instance.current) {
      return;
    }

    instance.current.setMap(null);
    instance.current = null;
    prevRef.current = undefined;
  };

  const getOptions = () => {
    const content = renderToString(<>{children}</>);
    const markerOptions = {
      ...options,
      icon: {content},
    };

    return markerOptions;
  };

  const createOverlay = () => {
    if (instance.current) {
      return;
    }

    const markerOptions = getOptions();

    prevRef.current = markerOptions;
    instance.current = new naver.maps.Marker(markerOptions);
  };

  const updateOverlay = () => {
    if (!instance.current) {
      return;
    }

    const markerOptions = getOptions();

    if (isEqual(prevRef.current, markerOptions)) {
      return;
    }

    instance.current.setOptions(markerOptions);
  };

  useEffect(() => {
    if (!options?.map) return;

    if (instance.current) {
      updateOverlay();
    } else {
      createOverlay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    return () => {
      removeInstance();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    instance: instance.current,
  }));

  return null;
};

export const StaticMarker = memo(forwardRef(StaticMarkerFRRF));
export type StaticMarkerRef = ElementRef<typeof StaticMarker>;
